import React, { useRef, useState } from 'react';
import { itemOptions } from '../../../utils/artDetailsUtils';
import useOutsideClick from '../../../utils/use-outside-click';

interface WizardData {
  txtName?: string[];
  txtNumber?: string[];
  txtQTY?: number[];
  hOrderType?: string;
  hCharacter?: string;
  qty?: string;
  sku?: string;
  szQTYTypeToEdit?: string;
  szQTYToEdit?: string;
  szChars?: string;
  uofm?: string;
  [key: string]: any;
}

interface RosterProps {
  wizardData: WizardData;
  isPrintView?: boolean;
}

interface RowData {
  name?: string;
  number?: string;
  label?: string;
  quantity: number;
}

export const letters = [
  "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P",
  "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z", "&"
];
export const numbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
export const greekLetters = [
  "ALPHA", "BETA", "GAMMA", "DELTA", "EPSILON", "ZETA", "ETA", "THETA",
  "IOTA", "KAPPA", "LAMBDA", "MU", "NU", "XI", "OMICRON", "PI", "RHO",
  "SIGMA", "TAU", "UPSILON", "PHI", "CHI", "PSI", "OMEGA"
];

type MappingResult = {
  array: string[] | number[];
  type: string | null;
};

function normalizeText(text: string) {
  const name = text?.toLowerCase();
  switch (name) {
    case 'titlla': return '~';
    case 'circunflex': return 'ˆ';
    case 'tancat': return 'ˊ';
    case 'guio': return '-';
    default: return text;
  }
}

function getSymbolStyles(text?: string): string {
  if (!text) return "";

  const specialSymbols = ['ˆ', 'ˊ'];
  const allSymbols = ['~', 'ˆ', 'ˊ', '-'];

  if (!allSymbols.includes(text)) return "";

  const baseStyles = "text-[1.75rem] leading-5 block text-3xl";
  return specialSymbols.includes(text) ? `${baseStyles} translate-y-2.5` : baseStyles;
}

function validateMappingLength(mappingString: string): MappingResult {
  const lettersLength = 27; // Length of `letters`
  const numbersLength = 10; // Length of `numbers`
  const greekLettersLength = 24; // Length of `greekLetters`
  const mappingArray = mappingString.split(",").map(val => val.trim());
  const arrayLength = mappingArray.length;

  if (arrayLength === lettersLength) {
    return { array: letters, type: "Letters" };
  } else if (arrayLength === numbersLength) {
    return { array: numbers, type: "Numbers" };
  } else if (arrayLength === greekLettersLength) {
    return { array: greekLetters, type: "Greek Letters" };
  } else {
    return { array: [], type: null };
  }
}

function mapData(
  input: string,
  splitDelimiter: string = ",",
  title: string = "Pack Contents"
) {
  const values = input.split(splitDelimiter).map(val => (val ? Number(val) : undefined));
  const mappingBase = validateMappingLength(input);

  if (!mappingBase) {
    throw new Error("Error: Unable to determine the mapping base.");
  }

  while (values.length < mappingBase.array.length) values.push(undefined);

  const rows = values
    .map((value, index) => (value !== undefined ? { value, mapped: mappingBase.array[index] } : null))
    .filter((item): item is { value: number; mapped: string | number } => item !== null);

  const labelHeader = mappingBase.type || "";

  const mappedRows = rows.map(r => ({
    label: String(r.mapped),
    quantity: r.value,
  }));

  return {
    rows: mappedRows,
    headers: [labelHeader, "Quantity"],
    title,
  };
}

const processRosterData = (
  wizardData: WizardData
): { rows: RowData[]; headers: string[]; title: string } | null => {
  // Case 1 - sku and qty
  if (wizardData.qty && wizardData.sku && !wizardData.hOrderType) {
    const quantities = wizardData.qty.split(';').map((q) => parseInt(q, 10));
    const skus = wizardData.sku.split(';');
    const lastCharacters = skus.map((sku) => sku.slice(-1));

    // Check if they are letters or numbers
    const areLetters = lastCharacters.every((char) => /^[A-Za-z]$/.test(char));
    const areNumbers = lastCharacters.every((char) => /^\d$/.test(char));

    const labelHeader = areLetters
      ? 'Letters'
      : 'Numbers'

    if (areLetters || areNumbers) {
      const mappedRows = skus.map((sku, index) => ({
        label: sku.slice(-1),
        quantity: quantities[index] || 0,
      }));

      return {
        rows: mappedRows,
        headers: [labelHeader, 'Quantity'],
        title: 'Pack Contents',
      };
    }
  }

  // Case 2 - hOrderType, hCharacter and qty
  if (wizardData?.hOrderType && wizardData?.hOrderType != "greek"
    && wizardData?.hCharacter && wizardData?.qty) {
    let characters = wizardData.hCharacter.split(';');
    let quantities = wizardData.qty.split(';').map((q) => parseInt(q, 10));

    if (wizardData?.hAllColor && wizardData?.hShowDCFlag) {
      const keepItems = wizardData?.hShowDCFlag
        .split(';')
        .map((x: string) => x.trim() !== "");

      characters = characters?.filter((_,i) => keepItems[i]);
      quantities = quantities?.filter((_,i) => keepItems[i]);
    }

    const filteredRows = characters
      .map((character, index) => ({
        label: character.trim(),
        quantity: quantities[index] || 0,
      }))
      .filter((row) => row.label.length === 1);

    return {
      rows: filteredRows,
      headers: [wizardData.hOrderType, 'Quantity'],
      title: 'Pack Contents',
    };
  }

  // case 3 - Greek
  if (wizardData?.hOrderType && wizardData?.hOrderType == "greek") {
    const greekLettersMap: Record<string, string> = {};

    for (let index = 0; index < greekLetters.length; index++) {
      greekLettersMap[`${19 + index}`] = greekLetters[index]
    }

    let characters = wizardData.hCharacter?.split(';').filter(Boolean) ?? [];
    let quantities = wizardData.qty?.split(';').map(q => parseInt(q, 10));

    if (wizardData?.hAllColor && wizardData?.hShowDCFlag) {
      const numberOfItems = wizardData?.hShowDCFlag
        .split(';')
        .filter((x: string) => x.trim() !== "").length;

      characters = characters?.slice(0, numberOfItems);
      quantities = quantities?.slice(0, numberOfItems)
    }

    const greekRows = characters?.map((character, index) => ({
      name: greekLettersMap[character.trim()],
      quantity: quantities?.[index] || 0,
    }));

    return {
      rows: greekRows,
      headers: ["Greek Letters", "Quantity"],
      title: "Pack Contents",
    };
  }

  // Case 4 - szQTYToEdit, szQTYTypeToEdit (PreCut)
  if (wizardData?.szQTYToEdit && wizardData.szQTYTypeToEdit?.toLowerCase() !== "kit") {
    const { szQTYToEdit } = wizardData;
    return mapData(szQTYToEdit);
  }

  // Case 5 - szChars (SimStich)
  if (wizardData?.szChars && ["Piece"].includes(wizardData?.uofm ?? wizardData?.UOFM)) {
    const { szChars } = wizardData;
    return mapData(szChars);
  }

  // Case 6 - txtName, txtNumber, txtQTY
  if ((wizardData?.txtName || wizardData?.txtNumber) && wizardData?.txtQTY) {
    const { txtName, txtNumber, txtQTY } = wizardData;
    const rows: RowData[] = [];

    if (txtQTY) {
      for (let i = 0; i < txtQTY.length; i++) {
        rows.push({
          name: txtName?.[i],
          number: txtNumber?.[i],
          quantity: txtQTY[i],
        });
      }
    }

    const headers = [
      txtName ? 'Name' : null,
      txtNumber ? 'Number' : null,
      'Quantity',
    ].filter(Boolean) as string[];

    return { rows, headers, title: 'Name and/or Number List' };
  }

  // case 7 - qty, isBarcelona (Barcelona)
  if (wizardData?.qty && wizardData?.isBarcelona) {
    const rows: RowData[] = [];
    const qty = (typeof wizardData?.qty === "object" && wizardData?.qty !== null)
      ? wizardData.qty as { [key: string]: number }
      : {};

    Object.entries(qty).forEach(([key, quantity]) => {
      const itemText = itemOptions?.find((opt) => opt.Key === key)?.Text || key;

      rows.push({
        name: normalizeText(itemText),
        quantity,
      });
    });

    const allValues = rows.map((row) => row.name).filter((val): val is string => !!val);
    const areLetters = allValues.every((val) => typeof val === "string" && /^[A-Z]$/.test(val));
    const areNumbers = allValues.every((val) => typeof val === "string" && /^\d$/.test(val));

    const titleHeader = areLetters
      ? "Letters"
      : areNumbers
        ? "Numbers"
        : "Name";

    const headers = [titleHeader, "Quantity"];

    return { rows, headers, title: "Pack Contents" };
  }

  return null;
};

const generateCSVContent = (rows: RowData[]): string => {
  const csvRows = rows.map(row => {
    const values = [];
    if (row.number) values.push(row.number);
    if (row.name) values.push(row.name);
    if (row.label) values.push(row.label);
    return values.join(',');
  }).filter(row => row.length > 0);

  return csvRows.join('\n');
};

const generateCopyContent = (rows: RowData[]): string => {
  const copyLines = rows.map(row => {
    const parts = [];
    if (row.number) parts.push(row.number);
    if (row.name) parts.push(row.name);
    if (row.label) parts.push(row.label);
    return parts.join(', ');
  }).filter(line => line.length > 0);

  return copyLines.join('\n');
};

const downloadCSV = (content: string, filename: string = 'roster-data.csv') => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const copyToClipboard = async (content: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(content);
    return true;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
};

const RosterGrid: React.FC<RosterProps> = ({ wizardData, isPrintView = false }) => {
  const [showActions, setShowActions] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const menu = useRef<HTMLDivElement>(null);

  const rosterData = processRosterData(wizardData);
  if (!rosterData) {
    return null;
  }

  const { rows, headers, title } = rosterData;

  // Only show export/copy options if there's data to export (excluding quantity-only columns)
  const hasExportableData = rows.some(row => row.name || row.number || row.label);

  const handleExportCSV = () => {
    const csvContent = generateCSVContent(rows);
    if (csvContent) {
      downloadCSV(csvContent, `${title.toLowerCase().replace(/\s+/g, '-')}.csv`);
    }
  };

  const handleCopyToClipboard = async () => {
    const copyContent = generateCopyContent(rows);
    if (copyContent) {
      const success = await copyToClipboard(copyContent);
      if (success) {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 1500);
      }
    }
  };

  useOutsideClick(menu, () => {
    setShowActions(false);
  });

  return (
    <>
      <div className={`flex flex-col ${isPrintView && "text-sm"}`}>
        <div className="flex justify-between items-end">
          <h3 className={`uppercase text-[#F36E29] font-bold ${!isPrintView ? 'pt-[1.5em] pb-0 px-[1.5em]' : ''}`}>
            {title}
          </h3>

          {!isPrintView && hasExportableData && (
            <div className="relative pr-4" ref={menu}>
              <button
                onClick={() => setShowActions(!showActions)}
                className="flex items-center gap-2 px-3 py-1.5 text-[#F36E29] rounded-3xl border border-solid border-[#F36E29]"
                aria-label="Export options"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
                <svg className={`w-3 h-3 transition-transform duration-200 ${showActions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showActions && (
                <div
                  className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[185px] mr-4">
                  <button
                    onClick={handleExportCSV}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-[#E6E7E9]"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download CSV
                  </button>
                  <button
                    onClick={handleCopyToClipboard}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-[#E6E7E9] relative"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy to Clipboard
                    {copySuccess && (
                      <span className="absolute right-2 text-sm text-[#F36E29] font-medium">
                        ✓
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className={`bg-white w-full pt-6 ${!isPrintView ? 'px-5' : ''}`}>
          <table className="table-auto w-full">
            <thead>
              <tr className="bg-[#f2f2f2] border-b-[rgb(218,218,218)] border-b border-solid rounded-xl">
                {headers.map((header, index) => (
                  <th
                    key={index}
                    className={`px-4 ${!isPrintView ? 'py-2' : 'py-1'} text-left text-[black] font-medium capitalize`}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={index}
                  className="border-b-[rgb(218,218,218)] border-b border-solid px-0 py-[0.5em] font-semibold text-[gray]"
                >
                  {row.name && <td className={`px-4 ${!isPrintView ? 'py-2' : 'py-1'} ${getSymbolStyles(row.name)}`}>{row.name}</td>}
                  {row.number && <td className={`px-4 ${!isPrintView ? 'py-2' : 'py-1'}`}>{row.number}</td>}
                  {row.label && <td className={`px-4 ${!isPrintView ? 'py-2' : 'py-1'}`}>{row.label}</td>}
                  <td className={`px-4 ${!isPrintView ? 'py-2' : 'py-1'}`}>{row.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default RosterGrid;